import type { ReactNode } from 'react';
import { Link } from 'react-router';
import AnchorLink from '@/components/common/anchor-link';
import PicturizedImage from '@/components/common/picturized-image';
import { emoticons } from '@/components/symbol/emoticon/emoticons';
import ROUTES from '@/routes';
import { splitWithMatches } from '@/utils/array';

export function escapeRegExpString(str: Nullable<string>) {
  if (!str) return '';
  return str.replaceAll(/[-[\]/{}()*+?.\\^$|]/g, '\\$&');
}

function generateMentionRegExp(members: MentionMember[]): RegExp {
  let names = members
    .flatMap(({ userName, groupName }) => [userName, groupName])
    .filter(Boolean)
    .concat(['所有人', 'All'])
    .map((name) => `@${escapeRegExpString(name)}`)
    .join('|');

  if (members.length > 0) names += '|@\\S+';

  return RegExp(`(?:^|(?<=\\s))(${names})`, 'i');
}

const emojiRegExp = /\[e\d+]/gi;
const linkRegExp =
  /(?<=\s|^)(?!\S*@\S*)(?!\S*\p{Script=Han})(?:[a-z][a-z0-9+.-]*:\/\/[^\s\p{Script=Han}@]+|(?:(?:[A-Za-z0-9-]+\.)+[A-Za-z]{2,}|\d{1,3}(?:\.\d{1,3}){3})(?::\d{1,5})?(?:\/[^\s\p{Script=Han}@]*)?)/gu;
const mentionEveryoneRegExp = generateMentionRegExp([]);
const linkBreakRegExp = /\r\n|\r|\n/gi;

export enum ScriptType {
  LineBreak = 0,
  Emoticon = 1,
  Link = 2,
  Mention = 3,
}

type ScriptNode = string | [ScriptType, string];
type ScriptNodes = Array<ScriptNode>;

type MentionMember = {
  id: number;
  groupId: number | undefined;
  groupName: Nullable<string>;
  userName: string;
};

export function transformSpecialScripts(
  id: number,
  message: string,
  members: MentionMember[] = [],
  transforms: ScriptType[] = [ScriptType.LineBreak, ScriptType.Link, ScriptType.Emoticon, ScriptType.Mention],
): ReactNode {
  let instructions: ScriptNodes = [message];

  // 處理 Emoticon
  if (transforms.includes(ScriptType.Emoticon)) {
    instructions = instructions.flatMap((node) => {
      if (typeof node !== 'string' || node === '') return [node];
      if (!emojiRegExp.test(node)) return [node];

      const matches = node.match(emojiRegExp);
      if (!matches) return [node];

      const parts = node.split(emojiRegExp);
      const result: ScriptNodes = [];

      parts.forEach((part, i) => {
        result.push(part);
        const emoji = matches[i];
        if (i < matches.length && emoji !== undefined) {
          if (emoji in emoticons) {
            result.push([ScriptType.Emoticon, emoji]);
          } else {
            result.push(emoji);
          }
        }
      });

      return result;
    });
  }

  // 處理 Link
  if (transforms.includes(ScriptType.Link)) {
    instructions = instructions.flatMap((node) => {
      if (typeof node !== 'string' || node === '') return [node];

      const result: ScriptNodes = [];
      let lastIndex = 0;
      for (const match of node.matchAll(linkRegExp)) {
        const index = match.index;
        if (index !== undefined && index > lastIndex) result.push(node.slice(lastIndex, index));
        result.push([ScriptType.Link, match[0]]);
        lastIndex = index + match[0].length;
      }
      if (lastIndex < node.length) result.push(node.slice(lastIndex));

      return result.length > 0 ? result : [node];
    });
  }

  // 處理 Mention
  if (transforms.includes(ScriptType.Mention) && Array.isArray(members)) {
    const mentionRegExp = generateMentionRegExp(members);

    instructions = instructions.flatMap((node) => {
      if (typeof node !== 'string' || node === '') return [node];
      if (!mentionRegExp.test(node)) return [node];

      return splitWithMatches(node, mentionRegExp).map((part) =>
        part.startsWith('@') ? ([ScriptType.Mention, part] as [ScriptType, string]) : part,
      );
    });
  }

  // 處理 LineBreak (最後)
  if (transforms.includes(ScriptType.LineBreak)) {
    instructions = instructions.flatMap((node) => {
      if (typeof node !== 'string' || node === '') return [node];

      const parts = node.split(linkBreakRegExp);
      const result: ScriptNodes = [];

      parts.forEach((line, i) => {
        if (line !== '') result.push(line);
        if (i < parts.length - 1) result.push([ScriptType.LineBreak, '']);
      });

      return result;
    });
  }

  // 渲染節點
  return instructions
    .map<ReactNode>((node, index, array) => {
      if (typeof node === 'string') return node;

      switch (node[0]) {
        case ScriptType.LineBreak:
          return <br key={`${id}-LineBreak-${index}`} />;
        case ScriptType.Emoticon: {
          const imageData = emoticons[node[1]];
          return imageData ? (
            <PicturizedImage
              key={`${id}-Emoji-${index}`}
              className='inline-block data-quote:size-5 size-9'
              picture={imageData}
              alt={node[1]}
            />
          ) : null;
        }
        case ScriptType.Link:
          return (
            <AnchorLink
              direct={array.length === 1}
              key={`${id}-Link-${index}`}
              className='text-blue-600 break-all'
              href={node[1]}>
              {node[1]}
            </AnchorLink>
          );
        case ScriptType.Mention: {
          if (node[1].match(mentionEveryoneRegExp)) {
            return (
              <span
                key={`${id}-Mention-${index}`}
                className='text-blue-600'>
                {node[1]}
              </span>
            );
          }

          const mentionedMember = members.find(({ groupName, userName }) => {
            const name = node[1].slice(1);
            return groupName === name || userName === name;
          });

          return mentionedMember ? (
            <Link
              to={ROUTES.CONTACT.USERS.BY_ID.$buildPath({
                params: { userId: mentionedMember.id },
                searchParams: { groupId: mentionedMember.groupId },
              })}
              key={`${id}-Mention-${index}`}
              className='text-blue-600'>
              {node[1]}
            </Link>
          ) : (
            <span
              key={`${id}-Mention-${index}`}
              className='text-neutral-500'>
              {node[1]}
            </span>
          );
        }
        default:
          return node;
      }
    })
    .filter(Boolean);
}
